/* Dealora product enhancements: use existing Shopify option controls and theme gallery. */
(() => {
  'use strict';
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  class ScrollCue extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.accordion__content');
      this.button = this.querySelector('button');
      if (!this.content || !this.button) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.update = () => {
        this.button.hidden = this.content.scrollHeight - this.content.clientHeight - this.content.scrollTop < 3 || this.content.clientHeight === 0;
      };
      this.content.tabIndex = 0;
      this.content.setAttribute('role', 'region');
      this.content.setAttribute('aria-label', this.closest('details')?.querySelector('.accordion__title')?.textContent.trim() || 'Product information');
      this.content.addEventListener('scroll', this.update, { passive: true, signal });
      this.closest('details')?.addEventListener('toggle', this.update, { signal });
      this.button.addEventListener('click', () => this.content.scrollBy({ top: Math.max(80, this.content.clientHeight * .7), behavior: reduced() ? 'instant' : 'smooth' }), { signal });
      this.resize = new ResizeObserver(this.update);
      this.resize.observe(this.content);
      // Observe content as well as the container: images and rich text can grow after opening.
      this.content.querySelectorAll('img, p, ul, ol, table').forEach(node => this.resize.observe(node));
      this.mutation = new MutationObserver(this.update);
      this.mutation.observe(this.content, { childList: true, subtree: true, characterData: true });
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); this.mutation?.disconnect(); }
  }
  class GalleryDots extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      this.viewer = this.gallery?.querySelector('product-gallery');
      this.slider = this.viewer?.querySelector('[id^="Slider-Gallery"]');
      if (!this.slider) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.refresh = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.render());
      };
      this.slider.addEventListener('scroll', this.refresh, { passive: true, signal });
      this.viewer.addEventListener('slideChanged', this.refresh, { signal });
      window.addEventListener('resize', this.refresh, { passive: true, signal });
      this.addEventListener('click', e => {
        const button = e.target.closest('button[data-media-id]');
        if (!button) return;
        const target = [...this.slider.children].find(el => el.dataset.mediaId === button.dataset.mediaId);
        if (!target) return;
        this.gallery.setActiveMedia?.(target.dataset.mediaId, false, false);
        this.slider.scrollTo({ left: target.offsetLeft - this.slider.firstElementChild.offsetLeft, behavior: reduced() ? 'instant' : 'smooth' });
        // A stacked desktop gallery needs vertical navigation rather than a horizontal scroll.
        if (matchMedia('(min-width:750px)').matches && this.gallery.dataset.desktopLayout === 'stacked') target.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'instant' : 'smooth' });
        this.active = target.dataset.mediaId;
        this.paint();
      }, { signal });
      this.mutation = new MutationObserver(this.refresh);
      this.mutation.observe(this.slider, { subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'], childList: true });
      this.resize = new ResizeObserver(this.refresh); this.resize.observe(this.slider);
      this.refresh();
    }
    render() {
      const items = [...this.slider.children].filter(el => el.matches('[data-media-id]') && getComputedStyle(el).display !== 'none' && !el.hidden);
      this.hidden = items.length < 2;
      this.viewer.classList.toggle('dl-has-dots', !this.hidden && getComputedStyle(this).display !== 'none');
      const key = items.map(el => el.dataset.mediaId).join('|');
      if (key !== this.key) {
        this.key = key;
        this.replaceChildren(...items.map((el, i) => {
          const b = document.createElement('button'); b.type = 'button'; b.dataset.mediaId = el.dataset.mediaId;
          b.setAttribute('aria-label', `View media ${i + 1} of ${items.length}`);
          b.setAttribute('aria-controls', el.id); return b;
        }));
      }
      if (items.length) {
        const bounds = this.slider.getBoundingClientRect();
        const horizontal = this.slider.scrollWidth > this.slider.clientWidth + 2;
        const nearest = horizontal ? items.reduce((a, b) => Math.abs(b.getBoundingClientRect().left - bounds.left) < Math.abs(a.getBoundingClientRect().left - bounds.left) ? b : a) : items.find(el => el.classList.contains('is-active')) || items[0];
        this.active = nearest.dataset.mediaId;
      }
      this.paint();
    }
    paint() { [...this.children].forEach(b => { const on = b.dataset.mediaId === this.active; b.setAttribute('aria-current', String(on)); }); }
    disconnectedCallback() { this.abort?.abort(); this.mutation?.disconnect(); this.resize?.disconnect(); cancelAnimationFrame(this.frame); }
  }
  class ModelPicker extends HTMLElement {
    connectedCallback() {
      this.brand = this.querySelector('[data-brand-select]');
      try { this.brands = JSON.parse(this.querySelector('[data-brands]').textContent).split(/\r?\n/).map(line => line.split('|').map(x => x.trim())).filter(x => x[0]).map(([label, prefix]) => ({ label, prefix: prefix ?? label })); } catch { this.brands = []; }
      this.brand.replaceChildren(...this.brands.map(({ label }, i) => { const op = document.createElement('option'); op.value = String(i); op.textContent = label; return op; }));
      this.abort = new AbortController();
      this.brand.addEventListener('change', () => this.apply(true), { signal: this.abort.signal });
      this.mutation = new MutationObserver(() => this.apply(false));
      // Theme replaces variant-selects after every selection. Keep the brand control outside it.
      this.mutation.observe(this, { childList: true });
      this.apply(false);
    }
    apply(userChange) {
      const controls = [...this.querySelectorAll('variant-selects select')];
      this.model = controls.find(x => x.name === `options[${this.dataset.modelName}]`);
      this.classList.toggle('dl-picker-ready', !!this.model && this.brands.length > 0);
      if (!this.model || !this.brands.length) { this.brand.closest('label').hidden = true; return; }
      this.brand.closest('label').hidden = false;
      this.model.closest('.product-form__input').classList.add('dl-model-field');
      const opts = [...this.model.options];
      opts.forEach(o => { if (!o.dataset.dlOriginal) { o.dataset.dlOriginal = o.textContent.trim(); o.dataset.dlDisabled = String(o.disabled); } });
      const matches = (o, b) => o.value.toLowerCase().startsWith(b.prefix.toLowerCase());
      [...this.brand.options].forEach((o, i) => { o.disabled = !opts.some(v => matches(v, this.brands[i])); });
      if (!userChange) {
        const selected = this.model.selectedOptions[0];
        const index = this.brands.findIndex(b => selected && matches(selected, b));
        if (index >= 0) this.brand.value = String(index);
      }
      const b = this.brands[Number(this.brand.value)]; if (!b) return;
      opts.forEach(o => {
        o.hidden = !matches(o, b);
        o.disabled = o.hidden || o.dataset.dlDisabled === 'true';
        o.textContent = this.dataset.trim === 'true' && !o.hidden ? o.dataset.dlOriginal.replace(new RegExp(`^${b.prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'i'), '') : o.dataset.dlOriginal;
      });
      if (userChange && this.model.selectedOptions[0]?.hidden) {
        const next = opts.find(o => !o.hidden && !o.disabled);
        if (next) { this.model.value = next.value; this.model.dispatchEvent(new Event('change', { bubbles: true })); }
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.mutation?.disconnect(); }
  }
  class Delivery extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      this.update = variant => {
        const unavailable = !variant;
        const backorder = variant?.available && variant.inventory_management && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue';
        const label = unavailable ? this.dataset.unavailable : backorder ? this.dataset.backorder : variant.available ? this.dataset.inStock : this.dataset.soldOut;
        const labelNode = this.querySelector('[data-stock-label]');
        if (labelNode.textContent !== label) labelNode.textContent = label;
        this.querySelector('.dl-stock').style.color = backorder ? this.dataset.backorderColor : variant?.available ? this.dataset.stockColor : this.dataset.soldColor;
        const estimate = this.querySelector('[data-delivery-estimate]'); if (estimate) estimate.hidden = !variant?.available;
      };
      this.read = () => {
        const json = this.product?.querySelector('variant-selects[data-primary] [data-dl-stock]') || this.product?.querySelector('variant-selects[data-primary] [data-selected-variant]');
        if (!json) return; // Single default variant keeps the server-rendered status.
        try { this.update(JSON.parse(json.textContent)); } catch { this.update(null); }
      };
      this.observer = new MutationObserver(this.read);
      if (this.product) this.observer.observe(this.product, { childList: true, subtree: true });
      this.read();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  for (const [tag, type] of [['dealora-scroll-cue', ScrollCue], ['dealora-gallery-dots', GalleryDots], ['dealora-model-picker', ModelPicker], ['dealora-delivery', Delivery]]) if (!customElements.get(tag)) customElements.define(tag, type);
})();
